"use client";

import React from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import {
  Layers,
  ChevronRight,
  ArrowLeft,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useCategoryPage } from "@/modules/public/home/hooks/usePublicData";
import ProductGrid from "@/modules/public/home/components/productspage/ProductGrid";
import ServicesGrid from "@/modules/public/home/components/productspage/ServicesGrid";

import { Category, Subcategory, Product } from "@/modules/public/home/types/public.types";

interface CategoryClientProps {
  categorySlug: string;
  initialData?: {
    category: Category;
    subcategories: Subcategory[];
    products: Product[];
    totalProducts: number;
  };
}

export default function CategoryClient({ categorySlug, initialData }: CategoryClientProps) {
  const router = useRouter();

  const {
    category,
    subcategories,
    products,
    filteredProducts,
    loading,
    loadingMore,
    error,
    searchQuery,
    setSearchQuery,
    hasMore,
    hasProducts,
    loadMoreProducts,
  } = useCategoryPage(categorySlug, initialData);

  const observerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (loading || loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreProducts();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = observerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [loading, loadingMore, hasMore, loadMoreProducts]);

  if (loading) {
    return (
      <div className="min-h-[75vh] flex flex-col items-center justify-center bg-gray-55">
        <Loader2 className="w-12 h-12 text-green-605 animate-spin mb-4" />
        <p className="text-gray-655 font-bold text-sm">Opening Category Catalog...</p>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 bg-gray-55">
        <div className="bg-white p-8 rounded-xl shadow-xs border border-gray-200 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Category Not Found</h2>
          <p className="text-gray-655 mb-6">{error || "The category you are looking for does not exist."}</p>
          <button
            onClick={() => router.push("/")}
            className="inline-flex items-center gap-2 bg-green-600 text-white font-bold px-6 py-2.5 rounded-md hover:bg-green-700 transition"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 via-[#f2faf5] to-slate-50 min-h-screen pb-20">
      {/* Breadcrumbs Bar */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 py-3.5 sticky top-0 z-10 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 overflow-x-auto whitespace-nowrap">
            <Link href="/" className="hover:text-emerald-600 transition-colors font-medium">Home</Link>
            <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            <span className="font-bold text-emerald-700 truncate max-w-xs">{category.name}</span>
          </nav>
        </div>
      </div>

      {/* Category Details Section (Hero) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          {/* Left side: Content */}
          <div className="md:col-span-7 space-y-4">
            <h1 className="text-2xl sm:text-3xl font-black text-green-600">
              {category.title || category.name}
            </h1>

            {/* Mobile Image (visible on mobile, hidden on desktop) */}
            <div className="block md:hidden w-full flex justify-center my-2">
              <div className="w-full max-w-[400px] aspect-square bg-white rounded-sm border border-slate-100 overflow-hidden relative group shadow-sm flex items-center justify-center p-3">
                {category.imageUrl ? (
                  <Image
                    src={category.imageUrl}
                    alt={category.name}
                    fill
                    sizes="(max-width: 768px) 100vw, 400px"
                    className="object-cover rounded-sm group-hover:scale-103 transition-transform duration-700 ease-out p-3"
                  />
                ) : (
                  <Layers className="w-20 h-20 text-slate-350 animate-pulse" />
                )}
              </div>
            </div>

            <p className="text-gray-900 text-sm sm:text-base leading-relaxed text-justify">
              {category.description ||
                `Explore high-quality products and premium solutions under the ${category.name} category. Connect directly with verified vendors for details and quotes.`}
            </p>
          </div>

          {/* Right side: Image (Desktop only) */}
          <div className="hidden md:flex md:col-span-5 w-full h-full justify-center">
            <div className="w-full max-w-[400px] aspect-square bg-white rounded-sm border border-slate-100 overflow-hidden relative group shadow-sm flex items-center justify-center p-3">
              {category.imageUrl ? (
                <Image
                  src={category.imageUrl}
                  alt={category.name}
                  fill
                  sizes="(max-width: 768px) 100vw, 400px"
                  className="object-cover rounded-sm group-hover:scale-103 transition-transform duration-700 ease-out p-3"
                />
              ) : (
                <Layers className="w-20 h-20 text-slate-350 animate-pulse" />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 space-y-14">
        {/* Subcategories Grid */}
        {subcategories.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-7 bg-emerald-600 rounded-full" />
              <h2 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
                Explore Subcategories
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {subcategories.map((sub) => (
                <div
                  key={sub._id}
                  onClick={() => router.push(`/${category.slug}/${sub.slug}`)}
                  className="bg-white hover:bg-emerald-50/5 border border-slate-200 hover:border-emerald-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer transition-all duration-300 rounded-xl overflow-hidden group flex flex-col justify-between h-full text-left"
                >
                  <div className="w-full">
                    {/* Full-width Image top (ratio 16:9 or similar) */}
                    <div className="w-full aspect-[16/10] bg-slate-50 overflow-hidden border-b border-slate-100 relative shrink-0">
                      {sub.imageUrl ? (
                        <Image
                          src={sub.imageUrl}
                          alt={sub.name}
                          fill
                          sizes="(max-width: 768px) 100vw, 300px"
                          className="object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-emerald-50/30">
                          <Layers className="w-10 h-10 text-emerald-600" />
                        </div>
                      )}
                    </div>

                    {/* Content Section */}
                    <div className="p-5 space-y-2">
                      <h3 className="font-bold text-sm sm:text-base text-emerald-700 group-hover:text-emerald-800 transition-colors line-clamp-1 leading-tight">
                        {sub.name}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-500 line-clamp-2 leading-relaxed min-h-[2.5rem]">
                        {sub.description || `Organic, high-quality agricultural solutions and formulations categorized under ${sub.name}.`}
                      </p>
                    </div>
                  </div>

                  {/* Explore Button */}
                  <div className="px-5 pb-5 pt-1 w-full">
                    <button
                      className="w-full bg-[#0f9648] hover:bg-[#0b6330] text-white font-bold py-2.5 px-4 rounded-full text-xs sm:text-sm shadow-xs transition duration-200 cursor-pointer text-center"
                    >
                      Explore
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <section className="bg-gray-100 py-10 mt-12 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Products/Services Grid */}
          {category.slug === "agri-services" ? (
            <ServicesGrid
              services={filteredProducts}
              title={`Services in ${category.name}`}
              searchPlaceholder={`Search in ${category.name}...`}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              hasServices={hasProducts}
              categorySlug={category.slug}
            >
              {/* Infinite Scroll Trigger */}
              <div ref={observerRef} className="h-16 flex justify-center items-center mt-6">
                {loadingMore ? (
                  <div className="flex items-center gap-2 text-slate-500 font-medium text-sm">
                    <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                    <span>Loading more services...</span>
                  </div>
                ) : !hasMore && products.length > 0 ? (
                  <p className="text-slate-400 text-xs sm:text-sm font-medium italic">
                    Showing all {products.length} services
                  </p>
                ) : null}
              </div>
            </ServicesGrid>
          ) : (
            <ProductGrid
              products={filteredProducts}
              title={`Products in ${category.name}`}
              searchPlaceholder={`Search in ${category.name}...`}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              hasProducts={hasProducts}
              categorySlug={category.slug}
            >
              {/* Infinite Scroll Trigger */}
              <div ref={observerRef} className="h-16 flex justify-center items-center mt-6">
                {loadingMore ? (
                  <div className="flex items-center gap-2 text-slate-500 font-medium text-sm">
                    <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                    <span>Loading more products...</span>
                  </div>
                ) : !hasMore && products.length > 0 ? (
                  <p className="text-slate-400 text-xs sm:text-sm font-medium italic">
                    Showing all {products.length} products
                  </p>
                ) : null}
              </div>
            </ProductGrid>
          )}
        </div>
      </section>
    </div>
  );
}
